import { useRef, useState, useContext, type ReactNode } from "react";
import { AiFillFlag } from "react-icons/ai";
import { type SxProps, type Theme } from "@mui/material/styles";

import { CommentsHeader } from "containers/admin/clients/touchpoint/components/comments/comment-header/comment-header";
import CommentsBody from "containers/admin/clients/touchpoint/components/comments/comment-body/comment-body";

import { Button, LoadingButton } from "ui/button";
import { Box } from "ui/box";
import { CloseButton } from "ui/close-button";
import { Divider } from "ui/divider";
import { Stack, type StackProps } from "ui/stack";
import { Popover, usePopoverClose } from "ui/popover";
import { Typography } from "ui/typography";
import { TextArea } from "ui/text-area";
import { Checkbox } from "ui/checkbox";
import { mergeSx } from "ui/merge-sx";

import CommentContext from "contexts/comment-context";

import { useCurrentUser } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";
import { classMerge } from "utilities/css";

import { colors } from "design-tokens";
import { Permission } from "types/auth";

import Icons from "assets";

const FAB_TRIGGER_SX: SxProps<Theme> = (theme) => ({
  height: theme.spacing(7),
  gap: 1,
  borderRadius: "9999px",
  "& svg": { width: 24, height: 24 },
});

const FAB_TRIGGER_ICON_ONLY_SX: SxProps<Theme> = mergeSx(FAB_TRIGGER_SX, { aspectRatio: "1 / 1" });

const FAB_PANEL_SX: SxProps<Theme> = { maxHeight: "calc(100vh - 13rem)", overflowY: "auto" };

export const GeneralCommentsFAB = () => {
  const {
    isFABOpen,
    setIsFABOpen,
    commentData,
    commentsSummary,
    loadComments,
    commentFilter,
    lastActiveCommentId,
    isClientUser,
  } = useContext(CommentContext);

  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState<boolean>(false);
  const hasActionItem = !!commentsSummary?.actionItemCount;
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleLoadMoreComments = () => {
    // if we're on the last page, return false, else, continue & always return true.
    if (commentData.last) {
      return Promise.resolve(false);
    }
    if (isLoadingMoreComments) {
      return Promise.resolve(true);
    }

    setIsLoadingMoreComments(true);

    return loadComments({
      size: 25,
      page: commentData.page + 1,
      filter: commentFilter.value,
    })
      .then((results) => {
        return !results.last;
      })
      .catch(() => {
        return true;
      })
      .finally(() => {
        setIsLoadingMoreComments(false);
      });
  };

  return (
    <Popover
      data-testid="fab-popover"
      placement="top"
      padding={0}
      keepPageInteractive
      sx={FAB_PANEL_SX}
      triggerPosition={{
        position: "fixed",
        bottom: 16,
        right: 32,
        zIndex: (theme) => theme.zIndex.modal,
      }}
      triggerElement={
        <Button
          ref={triggerRef}
          aria-label={isFABOpen ? "Hide Comments" : "Show Comments"}
          size="large"
          color={hasActionItem ? "error" : undefined}
          sx={commentsSummary?.openCount ? FAB_TRIGGER_SX : FAB_TRIGGER_ICON_ONLY_SX}>
          {hasActionItem ? <Icons.Flag /> : <Icons.Comment />}
          {!!commentsSummary?.openCount && <span>{commentsSummary.openCount}</span>}
        </Button>
      }>
      {commentsSummary?.commentCount > 0 ? (
        <FABContentWrapper>
          <CommentsHeader isClientUser={isClientUser} />
          <CommentsBody
            loadMoreComments={handleLoadMoreComments}
            lastActiveCommentId={lastActiveCommentId}
            isClientUser={isClientUser}
          />
        </FABContentWrapper>
      ) : (
        <EmptyCommentsPanel setIsOpen={setIsFABOpen} isClientUser={isClientUser} />
      )}
    </Popover>
  );
};

interface EmptyCommentsPanelProps {
  setIsOpen: (isOpen: boolean) => void;
  isClientUser: boolean;
}

const EmptyCommentsPanel = ({ setIsOpen, isClientUser }: EmptyCommentsPanelProps) => {
  const { submitNewComment, commentingDisabled } = useContext(CommentContext);
  const [comment, setComment] = useState<string>("");
  const [isActionItem, setIsActionItem] = useState<boolean>(true);
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentUser = useCurrentUser();

  const canComment = hasPermission(currentUser, Permission.PERM_COMMENT_WRITE);

  const setInitialState = () => {
    setComment("");
    setIsActionItem(true);
    setIsHidden(false);
    setIsLoading(false);
  };

  const handleSubmit = () => {
    setIsLoading(true);
    const payload = {
      actionItem: isActionItem,
      hidden: !isHidden,
      text: comment,
    };
    submitNewComment(payload).finally(() => {
      setInitialState();
      setIsLoading(false);
    });
  };

  const handleCancel = () => {
    setInitialState();
    setIsOpen(false);
  };
  return (
    <FABContentWrapper>
      <FABHeader>
        <Typography variant="inherit" component="span">
          There are no comments on this item yet
        </Typography>
      </FABHeader>

      {canComment && !commentingDisabled && (
        <Stack spacing={2}>
          <TextArea
            data-testid="comment-input-main"
            rows={4}
            resize="none"
            placeholder="Add a comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            // need to add ref to the input to focus on it
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          {/* Stack spacing is 8px, not 4px: the checkbox no longer carries MUI's 9px padding. */}
          {!isClientUser && (
            <Stack spacing={1}>
              <Checkbox
                data-testid="comment-input-checkbox-hidden"
                checked={isHidden}
                onChange={setIsHidden}
                size="sm"
                label="Visible to client"
              />
              <Checkbox
                data-testid="comment-input-checkbox-actionitem"
                checked={isActionItem}
                onChange={setIsActionItem}
                size="sm"
                label="Flag as action item"
              />
            </Stack>
          )}
          <div className="flex gap-4 justify-end">
            <Button
              data-testid="comment-input-cancel-button"
              variant="outlined"
              onClick={handleCancel}>
              Cancel
            </Button>
            <LoadingButton
              data-testid="comment-input-submit-button"
              disabled={comment.length <= 0}
              state={isLoading ? "loading" : "default"}
              loadingMessage="Submitting..."
              successMessage="Submitted"
              errorMessage="Error. Try again."
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </div>
        </Stack>
      )}
    </FABContentWrapper>
  );
};

export const FABContentWrapper = ({ ...rest }: StackProps) => {
  return (
    <Stack
      divider={<FABDivider />}
      sx={{
        "& > *:not([data-no-padding])": {
          px: 3,
          py: 2,
        },
      }}
      {...rest}
    />
  );
};

type FABHeaderProps = {
  actionItemCount?: number;
  children?: ReactNode;
};

export const FABHeader = ({ actionItemCount = 0, children }: FABHeaderProps) => {
  const hasActionItems = actionItemCount > 0;
  const closePopover = usePopoverClose();
  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4" sx={{ color: "grey.900" }}>
          Comments
        </Typography>
        {hasActionItems && (
          <FABActionItemBadge actionItemCount={actionItemCount} className="mr-4" />
        )}
        <CloseButton aria-label="Close comments" onClick={closePopover} />
      </Stack>
      {children}
    </Stack>
  );
};

type FABActionItemBadgeProps = {
  actionItemCount: number;
  className?: string;
};

export const FABActionItemBadge = ({ actionItemCount, className }: FABActionItemBadgeProps) => {
  return (
    <Box
      data-testid="comments-header-action-item-count"
      className={classMerge(
        "flex items-center justify-between !bg-critical-700 text-white text-xs rounded-full lowercase px-2 py-1",
        className,
      )}>
      <AiFillFlag size={13} className="mr-1.5" />
      {actionItemCount} action item
      {actionItemCount === 1 ? "" : "s"}
    </Box>
  );
};

export const FABDivider = () => (
  <Divider data-no-padding sx={{ borderColor: colors.neutrals[500] }} />
);
